import { useAuthContext } from "../../auth/state/AuthContext";
import HeaderTechform from "../../shared/app-shell/HeaderTechform";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import logo from "../../shared/assets/Vector.svg";
import { ReactComponent as CustomIcon } from "../../shared/assets/disk.svg";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { toastSuccess } from "../../shared/components/ToastContainer";
import { log, logE, logV } from "../../shared/services/debug-logger";
import { errorLogger } from "../../shared/services/error-logger";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import { TechFormValidationSummary } from "../components/TechFormValidationSummary";
import { TechFormVesselDraftBoundary } from "../components/TechFormVesselDraftBoundary";
import StepIndicator from "../components/stepIndicator";
import { INITIAL_FORM_DATA } from "../models/constants";
import type {
  TechFormSubmissionDraft,
  TechFormSubmissionHydrationStatus,
} from "../models/tech-form-submission-data.models";
import { TECH_FORM_STEP_PAGES } from "../models/tech-form-validation-ui.constants";
import type { TechnicalQuestionnaireProps } from "../models/tech-form-validation-ui.models";
import type { TechFormValidationIssue, TechFormValidationValues } from "../models/tech-form-validation.models";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import { vesselDetailsFormInit } from "../models/tq1-vessel-details.models";
import { designDetailsFormInit } from "../models/tq2-design-details.models";
import { getDefaultDraftTableTq3, tq3DraftFromInit } from "../models/tq3-draft.models";
import { tq6SecaFormInit } from "../models/tq6-seca.models";
import { tq7SlowSteamingFormInit } from "../models/tq7-slow-steaming.models";
import { powerManagementInit, tq8PowerManagementFormInit } from "../models/tq8-power-management.models";
import { prefetchNextTechFormSectionData } from "../services/tech-form-section-data.service";
import { loadTechFormSubmissionDraft } from "../services/tech-form-submission-data.service";
import {
  saveTq1GenInfo,
  saveTq2VesselDesign,
  saveTq3VesselDraft,
  saveTq4AntifoulingDetails,
  saveTq5LastPortCalls,
  saveTq6Seca,
  saveTq7SlowSteaming,
  saveTq8PowerManagement,
} from "../services/tech-forms.service";
import {
  techFormStepCountAtom,
  setTechFormSubmissionDraftAtom,
  tq1VesselDetailsAtom,
  tq2DesignDetailsAtom,
  tq3DraftAtom,
  tq3DraftTableAtom,
  tq4AntiFoulingDetailsAtom,
  tq5PortCallsAtom,
  tq6SecaAtom,
  tq7SlowStreamingAtom,
  tq8PowerManagementAtom,
  tq8PowerManagementTableAtom,
} from "../state/tech-form.atoms";
import { focusTechFormIssue } from "../utils/focus-tech-form-issue.utils";
import { getCurrentTechFormValidationIssues } from "../utils/get-current-tech-form-validation-issues.utils";
import { mergeTechFormSubmissionDraft } from "../utils/merge-tech-form-submission-draft.utils";
import { techFormValidator } from "../utils/tech-form-validations";
import { Tq1VesselDetails } from "./Tq1VesselDetails";
import { Tq2DesignDetails } from "./Tq2DesignDetails";
import { Tq3Draft } from "./Tq3Draft";
import { Tq4Antifouling } from "./Tq4Antifouling";
import { Tq5PortCalls } from "./Tq5PortCalls";
import { Tq6Seca } from "./Tq6Seca";
import { Tq7SlowSteaming } from "./Tq7SlowSteaming";
import { Tq8PowerManagement } from "./Tq8PowerManagement";
import { Tq9Confirmation } from "./Tq9Confirmation";
import { datadogRum } from "@datadog/browser-rum";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { twMerge } from "tailwind-merge";

let _prevForm: any;
let _prevTable: any;

const TechnicalQuestionnaire: React.FC<TechnicalQuestionnaireProps> = ({
  isCompletedForm,
  routeBasePath,
  landingPagePath,
}) => {
  const theme = useAtomValue(themeAtom);
  const setTechFormStepCount = useSetAtom(techFormStepCountAtom);
  const setTechFormSubmissionDraft = useSetAtom(setTechFormSubmissionDraftAtom);
  const tq1VesselDetails = useAtomValue(tq1VesselDetailsAtom);
  const tq2DesignDetails = useAtomValue(tq2DesignDetailsAtom);
  const tq3Draft = useAtomValue(tq3DraftAtom);
  const tq3DraftTable = useAtomValue(tq3DraftTableAtom);
  const tq4AntiFoulingDetails = useAtomValue(tq4AntiFoulingDetailsAtom);
  const tq5PortCalls = useAtomValue(tq5PortCallsAtom);
  const tq6Seca = useAtomValue(tq6SecaAtom);
  const tq7SlowStreaming = useAtomValue(tq7SlowStreamingAtom);
  const tq8PowerManagement = useAtomValue(tq8PowerManagementAtom);
  const tq8PowerManagementTable = useAtomValue(tq8PowerManagementTableAtom);

  const totalSteps = 9;
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const { pageName, vesselName, imoNumber } = useParams();
  const [selectedIssue, setSelectedIssue] = useState<TechFormValidationIssue | null>(null);
  const [pendingNavigationStep, setPendingNavigationStep] = useState<number | null>(null);
  const [submissionHydrationStatus, setSubmissionHydrationStatus] = useState<TechFormSubmissionHydrationStatus>("idle");
  const [submissionHydrationAttempt, setSubmissionHydrationAttempt] = useState(0);
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);
  const activeRouteBasePath = routeBasePath ?? (isCompletedForm ? "/technicalForm/completed" : "/technicalForm");
  const techFormCacheMode = isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable;

  const stepToPageMap = TECH_FORM_STEP_PAGES;
  const pageToStepMap = Object.fromEntries(Object.entries(stepToPageMap).map(([k, v]) => [v, parseInt(k)]));

  const step = pageToStepMap[pageName || "vessel-detail"] || 1;
  const isSectionNavigationPending = pendingNavigationStep !== null;
  const isSubmissionHydrationPending =
    step === 9 && !isCompletedForm && !["ready", "error"].includes(submissionHydrationStatus);
  const isTechnicalFormBusy = isSectionNavigationPending || isSubmissionHydrationPending;
  logV("TechnicalQuestionnaire - BUILD", { step, busy: isTechnicalFormBusy });

  useEffect(() => {
    if (pendingNavigationStep === step) {
      setPendingNavigationStep(null);
    }
  }, [pendingNavigationStep, step]);

  const currentSubmissionDraft: TechFormSubmissionDraft = {
    tq1VesselDetails,
    tq2DesignDetails,
    tq3Draft,
    tq3DraftTable,
    tq4AntiFoulingDetails,
    tq5PortCalls,
    tq6Seca,
    tq7SlowStreaming,
    tq8PowerManagement,
    tq8PowerManagementTable,
  };

  useEffect(() => {
    if (isCompletedForm || step !== 9) {
      setSubmissionHydrationStatus("idle");
      return;
    }
    if (!imoNumber || !vesselName) {
      setSubmissionHydrationStatus("error");
      return;
    }

    let ignoreResult = false;
    const localDraft = currentSubmissionDraft;
    setSubmissionHydrationStatus("loading");
    log("STATE - loadTechFormSubmissionDraft - loading");
    void loadTechFormSubmissionDraft({ imoNumber, vesselName, mode: TECH_FORM_CACHE_MODES.editable })
      .then((persistedDraft) => {
        if (ignoreResult) return;
        setTechFormSubmissionDraft(mergeTechFormSubmissionDraft(localDraft, persistedDraft));
        setSubmissionHydrationStatus("ready");
        log("STATE - loadTechFormSubmissionDraft - ready");
      })
      .catch((error: unknown) => {
        if (ignoreResult) return;
        logE(new Error("TechnicalQuestionnaire - loadTechFormSubmissionDraft - failed"));
        setSubmissionHydrationStatus("error");
      });

    return () => {
      ignoreResult = true;
    };
    // The current draft is intentionally captured once per hydration attempt so
    // atom updates made by the completed hydration do not restart the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imoNumber, isCompletedForm, setTechFormSubmissionDraft, step, submissionHydrationAttempt, vesselName]);

  const getVesselDetailsForValidation = (submissionDraft: TechFormSubmissionDraft) => {
    const fallback = vesselDetailsFormInit(vesselName, imoNumber);
    const form = submissionDraft.tq1VesselDetails;
    const wlthc = form?.wlthc ?? {};

    return {
      ...fallback,
      ...form,
      wlthc: {
        ballast: { ...fallback.wlthc.ballast, ...wlthc.ballast },
        fullBallast: { ...fallback.wlthc.fullBallast, ...wlthc.fullBallast },
        fullyLaden: { ...fallback.wlthc.fullyLaden, ...wlthc.fullyLaden },
      },
    };
  };

  const validationValues: TechFormValidationValues = {
    schemaVersion: getVesselDetailsForValidation(currentSubmissionDraft).techFormSchemaVersion,
    sections: {
      1: getVesselDetailsForValidation(currentSubmissionDraft),
      2: { ...designDetailsFormInit, ...currentSubmissionDraft.tq2DesignDetails },
      3: { ...tq3DraftFromInit, ...currentSubmissionDraft.tq3Draft },
      4: { ...INITIAL_FORM_DATA, ...currentSubmissionDraft.tq4AntiFoulingDetails },
      6: { ...tq6SecaFormInit, ...currentSubmissionDraft.tq6Seca },
      7: { ...tq7SlowSteamingFormInit, ...currentSubmissionDraft.tq7SlowStreaming },
      8: { ...tq8PowerManagementFormInit, ...currentSubmissionDraft.tq8PowerManagement },
    },
    draftRows: (Array.isArray(currentSubmissionDraft.tq3DraftTable)
      ? currentSubmissionDraft.tq3DraftTable
      : getDefaultDraftTableTq3()
    ).map((row) => ({ ...row })),
    powerRows: (Array.isArray(currentSubmissionDraft.tq8PowerManagementTable)
      ? currentSubmissionDraft.tq8PowerManagementTable
      : powerManagementInit
    ).map((row) => ({ ...row })),
  };
  const shouldDeferSubmissionValidation = step === 9 && !isCompletedForm && submissionHydrationStatus !== "ready";
  const shouldSuppressSubmittedDraftValidation = step === 9 && hasSubmittedSuccessfully;
  const allIssues =
    shouldDeferSubmissionValidation || shouldSuppressSubmittedDraftValidation
      ? []
      : techFormValidator.validateAll(validationValues);
  if (
    !shouldDeferSubmissionValidation &&
    !shouldSuppressSubmittedDraftValidation &&
    String(validationValues.sections[1]?.imoNumber ?? "") !== String(imoNumber ?? "")
  ) {
    allIssues.unshift({
      section: 1,
      key: "imoNumber",
      label: "Vessel identity",
      message: "Return to vessel selection and reopen this vessel before submitting",
    });
  }
  // Completed questionnaires remain editable, but their navigation must not be
  // blocked by validation rules introduced after they were saved.
  const currentIssues = getCurrentTechFormValidationIssues(allIssues, step, isCompletedForm);
  const canGoToNext = isCompletedForm || currentIssues.length === 0;

  const selectIssue = (issue: TechFormValidationIssue) => {
    setSelectedIssue(issue);
    if (issue.section !== step) {
      setPendingNavigationStep(issue.section);
    }
    navigate(`${activeRouteBasePath}/${imoNumber}/${vesselName}/${stepToPageMap[issue.section]}`);
  };
  useEffect(() => {
    if (!selectedIssue || selectedIssue.section !== step) return;
    return focusTechFormIssue(selectedIssue.key);
  }, [selectedIssue, step]);

  const validateBeforeSubmit = async () => {
    if (isCompletedForm) return true;
    if (submissionHydrationStatus !== "ready") return false;
    if (allIssues.length) {
      log("TechnicalQuestionnaire - validateBeforeSubmit - blocked", { count: allIssues.length });
      selectIssue(allIssues[0]);
      return false;
    }
    // Confirmation can be opened directly. Persist the validated draft before submitting it.
    try {
      for (let section = 1; section <= 8; section += 1) await saveTqFormIfEdited(section, true);
    } catch {
      return false;
    }
    return !errorLogger.hasErrorsByCategory("TQ");
  };

  const goToNext = async () => {
    log("TechnicalQuestionnaire - goToNext", { step, blocked: !canGoToNext || isSectionNavigationPending });
    if (!canGoToNext || isSectionNavigationPending) {
      return;
    }

    if (!isCompletedForm) {
      datadogRum.addAction("techFormNext", {
        ext_user_email: email,
        vessel_name: vesselName,
        imo_number: imoNumber,
        currentStep: step,
      });
    }

    const nextStep = step + 1;
    const nextPage = stepToPageMap[nextStep];

    if (nextPage) {
      setPendingNavigationStep(nextStep);
      try {
        await saveTqFormIfEdited(step);
        navigate(`${activeRouteBasePath}/${imoNumber}/${vesselName}/${nextPage}`);
      } catch (error) {
        setPendingNavigationStep(null);
        logE(new Error("TechnicalQuestionnaire - goToNext - failed"));
      }
    }
  };

  const saveAndClose = () => {
    log("TechnicalQuestionnaire - saveAndClose", { step });
    setTechFormStepCount(step);

    toastSuccess("Form saved succesfully");
    setTimeout(() => {
      goToLandingPage();
    }, 3000);
  };

  const goToPrev = async () => {
    log("TechnicalQuestionnaire - goToPrev", { step });
    if (isSectionNavigationPending) {
      return;
    }

    const prevStep = step - 1;
    const prevPage = stepToPageMap[prevStep];

    if (prevPage) {
      setPendingNavigationStep(prevStep);
      try {
        await saveTqFormIfEdited(step);
        navigate(`${activeRouteBasePath}/${imoNumber}/${vesselName}/${prevPage}`);
      } catch (error) {
        setPendingNavigationStep(null);
        logE(new Error("TechnicalQuestionnaire - goToPrev - failed"));
      }
    }
  };

  const navigate = useNavigate();

  const goToLandingPage = () => {
    log("TechnicalQuestionnaire - goToLandingPage");
    navigate(landingPagePath ?? `/landingPage`);
  };

  // Unlike the BQ form in the TQ we have 2 local storage objects for 2 of the pages, therefore we had to write a more verbose method.
  // Could be improved by moving the objects together in the same property (similar to BQ).
  // Will need change here and also in SF stored procedures to update the props names.
  const saveTqFormIfEdited = async (step: number, force = false) => {
    if (step === 1) {
      const _form = tq1VesselDetails ?? {};
      const formisEmpty = Object.keys(_form).length === 0;
      const hasChanges = force || JSON.stringify(_prevForm) !== JSON.stringify(_form);

      if (!formisEmpty && hasChanges) {
        await saveTq1GenInfo(imoNumber, vesselName, { tq1VesselDetails: _form, vesselName, imoNumber });
      }
    }

    if (step === 2) {
      const _form = tq2DesignDetails ?? {};
      const formisEmpty = Object.keys(_form).length === 0;
      const hasChanges = force || JSON.stringify(_prevForm) !== JSON.stringify(_form);

      if (!formisEmpty && hasChanges) {
        await saveTq2VesselDesign(imoNumber, vesselName, { tq2DesignDetails: _form, vesselName, imoNumber });
      }
    }

    if (step === 3) {
      const _form = tq3Draft ?? {};
      const _table = tq3DraftTable ?? {};
      const formisEmpty = Object.keys(_form).length === 0 && Object.keys(_table).length === 0;
      const hasChanges =
        force ||
        JSON.stringify(_prevForm) !== JSON.stringify(_form) ||
        JSON.stringify(_prevTable) !== JSON.stringify(_table);

      if (!formisEmpty && hasChanges) {
        await saveTq3VesselDraft(imoNumber, vesselName, {
          tq3Draft: _form,
          tq3DraftTable: _table,
          vesselName,
          imoNumber,
        });
      }
    }

    if (step === 4) {
      const _form = tq4AntiFoulingDetails ?? {};
      const formisEmpty = Object.keys(_form).length === 0;
      const hasChanges = force || JSON.stringify(_prevForm) !== JSON.stringify(_form);

      if (!formisEmpty && hasChanges) {
        await saveTq4AntifoulingDetails(imoNumber, vesselName, { tq4AntiFoulingDetails: _form, vesselName, imoNumber });
      }
    }

    if (step === 5) {
      const _form = tq5PortCalls ?? {};
      const formisEmpty = Object.keys(_form).length === 0;
      const hasChanges = force || JSON.stringify(_prevForm) !== JSON.stringify(_form);

      if (!formisEmpty && hasChanges) {
        await saveTq5LastPortCalls(imoNumber, vesselName, { tq5PortCalls: _form, vesselName, imoNumber });
      }
    }

    if (step === 6) {
      const _form = tq6Seca ?? {};
      const formisEmpty = Object.keys(_form).length === 0;
      const hasChanges = force || JSON.stringify(_prevForm) !== JSON.stringify(_form);

      if (!formisEmpty && hasChanges) {
        await saveTq6Seca(imoNumber, vesselName, { tq6Seca: _form, vesselName, imoNumber });
      }
    }

    if (step === 7) {
      const _form = tq7SlowStreaming ?? {};
      const formisEmpty = Object.keys(_form).length === 0;
      const hasChanges = force || JSON.stringify(_prevForm) !== JSON.stringify(_form);

      if (!formisEmpty && hasChanges) {
        await saveTq7SlowSteaming(imoNumber, vesselName, { tq7SlowStreaming: _form, vesselName, imoNumber });
      }
    }

    if (step === 8) {
      const _form = tq8PowerManagement ?? {};
      const _table = tq8PowerManagementTable ?? {};
      const formisEmpty = Object.keys(_form).length === 0 && Object.keys(_table).length === 0;
      const hasChanges =
        force ||
        JSON.stringify(_prevForm) !== JSON.stringify(_form) ||
        JSON.stringify(_prevTable) !== JSON.stringify(_table);

      if (!formisEmpty && hasChanges) {
        await saveTq8PowerManagement(imoNumber, vesselName, {
          tq8PowerManagement: _form,
          tq8PowerManagementTable: _table,
          vesselName,
          imoNumber,
        });
      }
    }
  };

  const cacheInitialForm = (form: any) => {
    _prevForm = form;
  };

  const cacheInitialFormAndTable = (form: any, table: any) => {
    _prevForm = form;
    _prevTable = table;
  };

  const handleSectionReady = useCallback(() => {
    if (!imoNumber || !vesselName || step >= 8) return;

    void prefetchNextTechFormSectionData({
      currentSection: step,
      imoNumber,
      vesselName,
      mode: techFormCacheMode,
    });
  }, [imoNumber, step, techFormCacheMode, vesselName]);

  const renderFormStep = () => {
    logV("TechnicalQuestionnaire - renderFormStep", { step });
    switch (step) {
      case 1:
        return (
          <Tq1VesselDetails
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            onSectionReady={handleSectionReady}
          />
        );
      case 2:
        return (
          <Tq2DesignDetails
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            onSectionReady={handleSectionReady}
          />
        );
      case 3:
        return (
          <Tq3Draft
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialFormAndTable}
            onSectionReady={handleSectionReady}
          />
        );
      case 4:
        return (
          <Tq4Antifouling
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            onSectionReady={handleSectionReady}
          />
        );
      case 5:
        return (
          <Tq5PortCalls
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            onSectionReady={handleSectionReady}
          />
        );
      case 6:
        return (
          <Tq6Seca
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            onSectionReady={handleSectionReady}
          />
        );
      case 7:
        return (
          <Tq7SlowSteaming
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            onSectionReady={handleSectionReady}
          />
        );
      case 8:
        return (
          <Tq8PowerManagement
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialFormAndTable}
            onSectionReady={handleSectionReady}
          />
        );
      case 9:
        if (submissionHydrationStatus === "error") {
          return (
            <TechFormSectionLoadError
              message="Your saved questionnaire could not be loaded. Retry before submitting."
              onRetry={() => setSubmissionHydrationAttempt((attempt) => attempt + 1)}
            />
          );
        }
        return (
          <Tq9Confirmation
            validateBeforeSubmit={validateBeforeSubmit}
            isCompletedForm={isCompletedForm}
            goToPrev={goToPrev}
            goToLandingPage={goToLandingPage}
            onSubmissionSucceeded={() => setHasSubmittedSuccessfully(true)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={twMerge("flex h-screen", theme.home.tailwind.backgound, theme.home.tailwind.textDefault)}>
      {isTechnicalFormBusy && (
        <div data-testid="tech-form-section-loading-layer" className="fixed inset-0 z-40">
          <LoadingWrapper isLoading loadingIndicatorClassName="text-green" loadingIndicatorSize="lg" />
          <span className="sr-only" role="status" aria-live="polite">
            Loading Technical Form section.
          </span>
        </div>
      )}
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden p-4">
        <HeaderTechform />
        <ToastContainer />
        <br />
        <header className="flex items-center justify-between">
          <div className="grid grid-flow-col grid-rows-2 gap-4">
            <div className="flex justify-start">
              <div>
                <img src={logo} alt="Description" className="mr-2" />
              </div>

              <div className={twMerge("mb-2 text-2xl font-bold md:mb-0 md:mr-4")}>Technical Form</div>
            </div>

            <div className="flex-col items-center md:flex">
              <StepIndicator currentStep={step} totalSteps={totalSteps} />
            </div>
          </div>
        </header>

        <main
          aria-busy={isTechnicalFormBusy}
          className={twMerge(
            "flex flex-1 flex-col overflow-y-auto border-y",
            "[&::-webkit-scrollbar]:w-2.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            theme.uwhcAssistant.tableScroll,
            theme.uwhcAssistant.scrolHover,
            theme.uwhcAssistant.border,
          )}
        >
          <TechFormValidationSummary issues={currentIssues} onSelect={selectIssue} />
          {renderFormStep()}
        </main>
        {step !== 9 && (
          <footer className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <PageFooter className="ml-0 p-0 pb-0" />

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {!isCompletedForm && (
                <button
                  onClick={saveAndClose}
                  className={twMerge(
                    "flex items-center rounded border px-4 py-2",
                    theme.uwhcAssistant.inputBorder,
                    theme.uwhcAssistant.inputBg,
                    theme.uwhcAssistant.text,
                  )}
                >
                  <CustomIcon className={twMerge("mr-2 h-6 w-6", theme.vesselDetails.info)} />
                  <span>Save and Close</span>
                </button>
              )}

              {step > 1 && (
                <button
                  disabled={isSectionNavigationPending}
                  className={twMerge(
                    "flex items-center rounded border px-4 py-2",
                    theme.uwhcAssistant.inputBorder,
                    theme.myVesselsTableRow.row.primary,
                    theme.headerComponent.tailwind.buttonText,
                  )}
                  onClick={goToPrev}
                >
                  Previous
                </button>
              )}

              {step < 9 && (
                <button
                  disabled={!canGoToNext || isSectionNavigationPending}
                  className={twMerge(
                    "rounded border px-4 py-2",
                    canGoToNext
                      ? twMerge(theme.uwhcAssistant.sendBg, theme.uwhcAssistant.sendText)
                      : twMerge(
                          "cursor-not-allowed opacity-60",
                          theme.headerComponent.tailwind.backButtonBg,
                          theme.uwhcAssistant.inputText,
                        ),
                    theme.uwhcAssistant.inputBorder,
                  )}
                  onClick={() => goToNext()}
                >
                  Next
                </button>
              )}
            </div>
          </footer>
        )}
        {step === 9 && <PageFooter />}
      </div>
    </div>
  );
};

const VesselScopedTechnicalQuestionnaire: React.FC<TechnicalQuestionnaireProps> = (props) => (
  <TechFormVesselDraftBoundary
    mode={props.isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable}
  >
    <TechnicalQuestionnaire {...props} />
  </TechFormVesselDraftBoundary>
);

export default VesselScopedTechnicalQuestionnaire;
